import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeCheckbox } from '@/components/ui/tee-checkbox';
import { TeeLoader } from '@/components/ui/tee-loader';
import {
  TeeTabs,
  TeeTabsContent,
  TeeTabsList,
  TeeTabsTrigger,
} from '@/components/ui/tee-tabs';
import {
  useCurrentWeek,
  useLogActivity,
  useSetTaskCompletion,
} from '@/hooks/useGamePlanTracker';
import type { CurrentWeekResponse, WeekTask } from '@/types/gamePlanTracker';
import { LogActivityModal } from './LogActivityModal';
import { GuideDrawer, type GuideContent } from './GuideDrawer';
import { SocialTab } from './SocialTab';
import { ActivityStream } from './ActivityStream';
import { AdjustPlanButton } from './AdjustPlanButton';
import { phaseIndexForWeek, planWeekOfDate } from '../plan.util';
import { buildWeekPosts, postTaskId } from '../currentWeek.social';

const microLabel =
  'text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-content-sub-title)]';

interface CurrentWeekBoardProps {
  locationId: string;
  initialData: CurrentWeekResponse;
  locationName?: string;
  switcher?: React.ReactNode;
}

export function CurrentWeekBoard({
  locationId,
  initialData,
  locationName,
  switcher,
}: CurrentWeekBoardProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'tasks';
  const setActiveTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next);
  };

  const [viewWeek, setViewWeek] = useState<number | undefined>(undefined);
  const [guide, setGuide] = useState<GuideContent | null>(null);
  const [logOpen, setLogOpen] = useState(false);

  const { data, isFetching } = useCurrentWeek(locationId, viewWeek);
  const res = data ?? initialData;

  const setCompletion = useSetTaskCompletion(locationId);
  const logActivity = useLogActivity(locationId);

  const { plan, banner, tasks } = res;
  const currentWeek = plan.currentWeek;
  const week = res.viewWeek;

  // Checkbox state comes from the server, and the refetch after a toggle takes
  // a beat — so without this the box visibly snaps back to its old state before
  // flipping again. Hold the owner's intent locally until the server data
  // agrees, keyed by completionId.
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const clearPending = (key: string) =>
    setPending((p) => {
      const { [key]: _drop, ...rest } = p;
      return rest;
    });

  const toggleTask = (task: WeekTask, completed: boolean) => {
    if (task.auto) return;
    // completionId, NOT id: catch-up ('cu-') and carried ('b1-') copies display
    // under a prefixed id but their doneness is stored against the base item.
    const key = task.completionId;
    setPending((p) => ({ ...p, [key]: completed }));
    setCompletion.mutate(
      { taskId: key, week, completed },
      { onSettled: () => clearPending(key) },
    );
  };
  const togglePost = (taskId: string, completed: boolean) => {
    setPending((p) => ({ ...p, [taskId]: completed }));
    setCompletion.mutate(
      { taskId, week, completed },
      { onSettled: () => clearPending(taskId) },
    );
  };

  // Owners can browse the full 16-week plan; the "Past/Upcoming week" badge and
  // viewed-week phase labelling keep the context clear.
  const minWeek = 1;
  const maxWeek = 16;
  const goWeek = (w: number) => {
    const clamped = Math.min(maxWeek, Math.max(minWeek, w));
    setViewWeek(clamped === currentWeek ? undefined : clamped);
  };

  const openTaskGuide = (task: WeekTask) => {
    if (!task.guide) return;
    setGuide({
      eyebrow: 'Guide',
      title: task.guide.guideTitle,
      intro: task.guide.guideIntro,
      bulletsTitle: 'Tips',
      bullets: task.guide.guideBullets,
      playbookAnchor: task.guide.fragmentAnchor,
    });
  };

  // Social posts read their done state from a flat id list, so fold the
  // in-flight toggles into it to get the same no-flicker behaviour as the
  // to-do checkboxes.
  const effectiveCompletedTaskIds = useMemo(() => {
    const ids = new Set(res.completedTaskIds);
    for (const [id, done] of Object.entries(pending)) {
      if (done) ids.add(id);
      else ids.delete(id);
    }
    return Array.from(ids);
  }, [res.completedTaskIds, pending]);

  const pct = Math.min(100, Math.round((banner.members / banner.goal) * 100));
  const currentPhaseIdx = phaseIndexForWeek(currentWeek);
  const viewedPhaseIdx = phaseIndexForWeek(week);
  const viewedPhase = plan.phases[viewedPhaseIdx];

  // Open-item counts for the tab badges.
  const tasksOpen = tasks.sections.reduce((n, s) => n + s.tasks.length, 0);
  const doneIds = new Set(effectiveCompletedTaskIds);
  const socialOpen = buildWeekPosts(week, banner.goal).filter(
    (p) => !doneIds.has(postTaskId(week, p.idx)),
  ).length;

  return (
    <>
      {switcher && <div className="flex justify-end mb-3">{switcher}</div>}

      {/* Forest hero — identity, countdown, pace, phase progress, KPIs */}
      <div className="relative overflow-hidden rounded-[12px] bg-[var(--color-forest)] p-6 mb-4">
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(rgba(251,246,236,0.12) 1.1px, transparent 1.1px)',
            backgroundSize: '15px 15px',
          }}
          aria-hidden
        />
        <div className="relative flex flex-col gap-5">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-gold-500)]">
                Your Game Plan{locationName ? ` · ${locationName}` : ''}
              </span>
              <div className="flex items-center gap-2">
                <h1 className="text-[30px] font-bold leading-none text-[var(--text-on-forest)]">
                  Week {week} of 16
                </h1>
                {week !== currentWeek && (
                  <span className="text-[11px] font-semibold uppercase tracking-wide rounded-full px-2.5 py-1 bg-[rgba(251,246,236,0.14)] text-[var(--text-on-forest)]">
                    {week < currentWeek ? 'Past week' : 'Upcoming'}
                  </span>
                )}
              </div>
              <p className="text-body-s text-[var(--text-on-forest-muted)]">
                Phase {viewedPhaseIdx + 1} of 5 · {viewedPhase.name} ·{' '}
                {viewedPhase.dateRange}
              </p>
            </div>
            <div className="text-right flex flex-col items-end gap-2">
              <div>
                <div className="text-[36px] font-extrabold leading-none text-[var(--text-on-forest)]">
                  <span className="text-[var(--color-gold-500)]">
                    {plan.weeksLeft}
                  </span>{' '}
                  weeks
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-on-forest-muted)] mt-1.5">
                  to Grand Opening · {plan.launchLabel}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-full px-3 py-1.5 bg-[rgba(251,246,236,0.14)] text-[var(--text-on-forest)]">
                  ● {banner.pace.label}
                </span>
                <AdjustPlanButton locationId={locationId} tone="forest" />
              </div>
            </div>
          </div>

          {/* Phase progress strip */}
          <div className="flex gap-1.5" aria-hidden>
            {plan.phases.map((p, i) => (
              <div
                key={p.key}
                className="h-1.5 flex-1 rounded-full"
                style={{
                  background:
                    i < currentPhaseIdx
                      ? 'var(--color-gold-500)'
                      : i === currentPhaseIdx
                      ? 'var(--text-on-forest)'
                      : i === viewedPhaseIdx
                      ? 'var(--color-gold-500)'
                      : 'rgba(251,246,236,0.18)',
                }}
              />
            ))}
          </div>

          <p className="text-body-s text-[var(--text-on-forest-muted)]">
            {banner.pace.narrative}
          </p>

          {/* KPI chips */}
          <div className="flex flex-wrap gap-3">
            <HeroStat label="Founding members">
              <div className="flex items-center gap-3">
                <MemberRing pct={pct} />
                <span className="text-[22px] font-bold text-[var(--text-on-forest)]">
                  {banner.members}
                  <span className="text-[14px] text-[var(--text-on-forest-muted)]">
                    {' '}
                    / {banner.goal}
                  </span>
                </span>
              </div>
            </HeroStat>
            <HeroStat label="Leads">
              <span className="text-[22px] font-bold text-[var(--text-on-forest)]">
                {banner.leads}
              </span>
            </HeroStat>
            <HeroStat label="Activities this week">
              <span className="text-[22px] font-bold text-[var(--text-on-forest)]">
                {banner.activitiesThisWeek}
              </span>
            </HeroStat>
          </div>

          {/* Cross-links to the other surfaces */}
          <div className="flex flex-wrap gap-5 pt-1">
            <HeroLink
              label="See the 16-week Roadmap →"
              onClick={() => navigate('/game-plan/overview')}
            />
            <HeroLink
              label="Track your numbers on the Goal Board →"
              onClick={() => navigate('/game-plan/goal-board')}
            />
          </div>
        </div>
      </div>

      {/* Week nav + log */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <TeeButton
            variant="secondary"
            size="s"
            text="← Previous"
            onClick={() => goWeek(week - 1)}
            disabled={week <= minWeek || isFetching}
          />
          <TeeButton
            variant="secondary"
            size="s"
            text="Next →"
            onClick={() => goWeek(week + 1)}
            disabled={week >= maxWeek || isFetching}
          />
          {week !== currentWeek && (
            <TeeButton
              variant="ghost"
              size="s"
              text="Back to this week"
              onClick={() => setViewWeek(undefined)}
            />
          )}
          {isFetching && <TeeLoader size="sm" />}
        </div>
        <TeeButton
          variant="primary"
          size="m"
          text="Log activity"
          onClick={() => setLogOpen(true)}
        />
      </div>

      <TeeTabs value={activeTab} onChange={setActiveTab} className="w-full">
        <TeeTabsList>
          <TeeTabsTrigger value="tasks">
            To-dos
            {tasksOpen > 0 && <TabCount n={tasksOpen} />}
          </TeeTabsTrigger>
          <TeeTabsTrigger value="social">
            Social
            {socialOpen > 0 && <TabCount n={socialOpen} />}
          </TeeTabsTrigger>
        </TeeTabsList>

        <TeeTabsContent value="tasks">
          <div className="pt-5 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-x-10 gap-y-6">
            {/* To-dos */}
            <div className="flex flex-col">
              {tasks.sections.map((section, i) => (
                <div
                  key={section.key}
                  className={
                    i > 0
                      ? 'border-t border-[var(--border-default)] pt-4 mt-1'
                      : ''
                  }
                >
                  <div className="flex flex-col gap-0.5 mb-1">
                    <span className={microLabel}>{section.title}</span>
                    {section.subtitle && (
                      <span className="text-body-s text-[var(--text-content-sub-title)]">
                        {section.subtitle}
                      </span>
                    )}
                  </div>
                  {section.tasks.length === 0 ? (
                    <p className="text-body-s text-[var(--text-content-sub-title)] py-1.5">
                      Nothing here right now.
                    </p>
                  ) : (
                    <div className="flex flex-col">
                      {section.tasks.map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          pendingChecked={pending[task.completionId]}
                          onToggle={(c) => toggleTask(task, c)}
                          onGuide={() => openTaskGuide(task)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Done — always visible at the bottom, light treatment */}
              {tasks.doneTasks.length > 0 && (
                <div className="border-t border-[var(--border-default)] mt-1 pt-3 opacity-70">
                  <span className={microLabel}>
                    Done · {tasks.doneTasks.length}
                  </span>
                  <div className="flex flex-col mt-0.5">
                    {tasks.doneTasks.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        pendingChecked={pending[task.completionId]}
                        onToggle={(c) => toggleTask(task, c)}
                        onGuide={() => openTaskGuide(task)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Activity stream — right column, borderless cards, no divider */}
            <div className="lg:self-start">
              <ActivityStream
                week={week}
                logs={res.logs}
                isCurrentWeek={week === currentWeek}
                onLog={() => setLogOpen(true)}
              />
            </div>
          </div>
        </TeeTabsContent>

        <TeeTabsContent value="social">
          <div className="pt-5">
            <SocialTab
              week={week}
              goal={banner.goal}
              currentWeek={currentWeek}
              todayDayOfPlan={plan.todayDayOfPlan}
              isUpcoming={plan.isUpcoming}
              completedTaskIds={effectiveCompletedTaskIds}
              onToggle={togglePost}
              onOpenGuide={setGuide}
            />
          </div>
        </TeeTabsContent>
      </TeeTabs>

      <LogActivityModal
        isOpen={logOpen}
        onClose={() => setLogOpen(false)}
        isSaving={logActivity.isPending}
        onSave={async (dto) => {
          const created = await logActivity.mutateAsync(dto);
          // Snap the view to the week the activity belongs to so it's always
          // visible right after logging (logs live in the week they happened).
          const w = planWeekOfDate(
            res.grandOpeningDate,
            String(created.date).slice(0, 10),
          );
          setViewWeek(w === currentWeek ? undefined : w);
        }}
        todayISO={new Date().toISOString().slice(0, 10)}
        planStartISO={plan.startISO}
        isUpcoming={plan.isUpcoming}
      />
      <GuideDrawer content={guide} onClose={() => setGuide(null)} />
    </>
  );
}

function HeroLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[13px] font-semibold text-[var(--color-gold-500)] hover:text-[var(--text-on-forest)] transition-colors"
    >
      {label}
    </button>
  );
}

function TabCount({ n }: { n: number }) {
  return (
    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold bg-[rgba(22,36,30,0.07)] text-[var(--text-content-sub-title)]">
      {n}
    </span>
  );
}

function HeroStat({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-[10px] border border-[rgba(251,246,236,0.14)] bg-[rgba(251,246,236,0.07)] px-4 py-3 min-w-[180px]">
      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-on-forest-muted)]">
        {label}
      </span>
      {children}
    </div>
  );
}

function MemberRing({ pct }: { pct: number }) {
  const r = 15.5;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(100, Math.max(0, pct)) / 100);
  return (
    <svg width="40" height="40" viewBox="0 0 36 36" className="flex-none">
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        stroke="rgba(251,246,236,0.18)"
        strokeWidth="4"
      />
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        stroke="var(--color-gold-500)"
        strokeWidth="4"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 18 18)"
      />
    </svg>
  );
}

function TaskRow({
  task,
  pendingChecked,
  onToggle,
  onGuide,
}: {
  task: WeekTask;
  /** Optimistic override while the toggle is in flight. */
  pendingChecked?: boolean;
  onToggle: (checked: boolean) => void;
  onGuide: () => void;
}) {
  const checked = pendingChecked ?? task.isDone;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[var(--border-default)] last:border-b-0">
      <div className="pt-0.5">
        <TeeCheckbox
          checked={checked}
          disabled={task.auto}
          onChange={onToggle}
          size="m"
        />
      </div>
      <div className="flex-1 flex flex-col gap-0.5">
        <div className="flex items-start justify-between gap-3">
          <span
            className={
              'text-body-m leading-snug ' +
              (checked
                ? 'text-[var(--text-content-sub-title)]'
                : 'text-[var(--text-content-title)]')
            }
          >
            {task.title}
          </span>
          {task.guide && (
            <TeeButton
              variant="ghost"
              size="s"
              text="Guide →"
              onClick={onGuide}
              className="shrink-0 !px-1 text-[var(--color-text-gold)]"
            />
          )}
        </div>
        {task.tip && !checked && (
          <p className="text-body-s text-[var(--text-content-sub-title)]">
            {task.tip}
          </p>
        )}
        <div className="flex items-center gap-2 flex-wrap empty:hidden">
          {task.dueLabel && (
            <span
              className={
                'text-[12px] ' +
                (task.dueState === 'late'
                  ? 'text-[var(--color-text-gold)] font-medium'
                  : task.dueState === 'today'
                  ? 'text-[var(--color-forest)] font-medium'
                  : 'text-[var(--text-content-sub-title)]')
              }
            >
              {task.dueLabel}
            </span>
          )}
          {task.progressLabel && (
            <span className="text-[12px] text-[var(--color-text-gold)]">
              {task.dueLabel ? '· ' : ''}
              {task.progressLabel}
            </span>
          )}
          {task.isDone && task.auto && (
            <span className="text-[12px] text-[var(--text-content-sub-title)]">
              {task.dueLabel || task.progressLabel ? '· ' : ''}
              Completed from your activity log
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
